import { BellRing, Eye, Megaphone, MessageSquare, RefreshCw, ShoppingCart } from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { orderStatus } from '@/theme/status';
import type { LeadCard } from '@/types/crm';
import {
  channelLabel,
  contactLabel,
  describeLeadMotivo,
  describeLeadPreview,
  describeLeadStats,
  formatRelativeTime,
  originLabel,
} from './crm-format';

/**
 * Una tarjeta del kanban (`LeadCard` en el contrato).
 *
 * Es un componente de PROPS: no lee hooks, ni sesión, ni red, así que el smoke lo puede pintar con
 * un lead inventado. Lo que DICE sale de `crm-format.ts`.
 *
 * La tarjeta del kanban no repite el estado (pega en la columna que ya lo dice): lo que sí muestra
 * es lo que hace falta para decidir sin abrir nada — el último mensaje, si el bot está pausado
 * (requiere atención humana), de dónde salió el estado (AUTO/MANUAL), cuánta conversación y cuántas
 * órdenes hay detrás, y el anuncio de origen cuando el cliente llegó por Click-to-WhatsApp.
 */

export interface LeadCardItemProps {
  lead: LeadCard;
  /** Abre la ficha: el "por qué" del estado, el historial y los últimos mensajes. */
  onOpen: (lead: LeadCard) => void;
  /** Reclasifica el lead a mano. */
  onStatus: (lead: LeadCard) => void;
}

export function LeadCardItem({ lead, onOpen, onStatus }: LeadCardItemProps) {
  const preview = describeLeadPreview(lead);
  const lastActivity = lead.lastMessageAt ?? lead.createdAt;
  const anuncio = lead.campaignId ?? lead.adId;

  return (
    <li className="rounded-panel border border-line bg-surface p-3 shadow-card transition-colors hover:border-line-strong">
      <div className="flex items-start justify-between gap-2">
        <button
          type="button"
          onClick={() => onOpen(lead)}
          className="min-w-0 flex-1 rounded-control text-left focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500"
          aria-label={`Ver el lead de ${contactLabel(lead)}`}
        >
          <span className="block truncate text-body font-medium text-ink">
            {contactLabel(lead)}
          </span>
          <span className="mt-0.5 block text-micro text-ink-faint">
            {channelLabel[lead.channel]} · {formatRelativeTime(lastActivity)}
          </span>
        </button>

        <span className="flex shrink-0 gap-1">
          <Button
            variant="ghost"
            size="icon"
            aria-label={`Ver el lead de ${contactLabel(lead)}`}
            onClick={() => onOpen(lead)}
          >
            <Eye size={16} />
          </Button>

          <Button
            variant="ghost"
            size="icon"
            aria-label={`Reclasificar el lead de ${contactLabel(lead)}`}
            onClick={() => onStatus(lead)}
          >
            <RefreshCw size={16} />
          </Button>
        </span>
      </div>

      <p className="mt-2 text-caption text-ink-soft">
        {preview ?? 'Sin mensajes todavía: la conversación aún no arrancó.'}
      </p>

      <div className="mt-2 flex flex-wrap items-center gap-1.5">
        {lead.requiereAtencion ? (
          <Badge tone="warning" dot icon={<BellRing size={12} />}>
            Requiere atención
          </Badge>
        ) : null}

        <Badge tone={lead.estadoLeadOrigen === 'MANUAL' ? 'brand' : 'neutral'}>
          {originLabel[lead.estadoLeadOrigen]}
        </Badge>

        {anuncio ? (
          <Badge tone="info" icon={<Megaphone size={12} />}>
            {anuncio}
          </Badge>
        ) : null}
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-micro text-ink-faint">
        <span className="inline-flex items-center gap-1">
          <MessageSquare size={12} aria-hidden="true" />
          {describeLeadStats(lead)}
        </span>

        {lead.ordenesTotal > 0 ? (
          <span className="inline-flex items-center gap-1">
            <ShoppingCart size={12} aria-hidden="true" />
            {lead.ultimaOrden
              ? `Última orden: ${orderStatus[lead.ultimaOrden.estado].label}`
              : 'Con órdenes'}
          </span>
        ) : null}
      </div>

      <p className="mt-1.5 truncate text-micro text-ink-muted" title={lead.estadoLeadMotivo ?? ''}>
        {describeLeadMotivo(lead.estadoLeadMotivo)}
      </p>
    </li>
  );
}
